import moment from 'moment';
import globals from '@/globals';
import PortalService from './portal.service';
import moneyFormat from './utils/moneyFormat';
import statusVerification from './utils/statusVerification';
import { partners } from '../../utils/partners';
import logger from '../../lib/logger';
import cookieService from '../../services/cookie';

function calculate({ value, interest, installments }) {
  const pv = value;
  // eslint-disable-next-line max-len
  const pmt = pv * ((((1 + interest) ** installments) * interest) / (((1 + interest) ** installments) - 1));
  return {
    numberOfInstallments: installments,
    installmentValue: parseFloat((pmt).toFixed(2)),
    interest,
    totalValue: parseFloat((installments * pmt).toFixed(2)),
  };
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function getDueDateAndActionAndLabel(order) {
  const hasDownPayment = order?.downPaymentBillet;
  const isDownPaymentPaid = order?.downPaymentBillet?.paymentStatus === 'PAID';
  const hasInstallments = !!order?.installments?.length > 0;
  const orderCanceled = order.status === 'CANCELED';
  const formatDate = (date) => moment(date).format('DD/MM/YYYY');
  const openLinkFn = (link) => () => window.open(link);
  const doNothingFn = () => { };
  if (hasDownPayment && !isDownPaymentPaid) {
    return {
      dueDate: `Vence em ${formatDate(order.downPaymentBillet.dueDate)}`,
      action: openLinkFn(order.downPaymentBillet.link),
      label: 'Pagar agora',
    };
  }
  if (hasInstallments && !orderCanceled) {
    const installmentToPay = order.installments.find((i) => i.paymentStatus !== 'PAID');
    if (installmentToPay) {
      return {
        dueDate: `Vence em ${formatDate(installmentToPay.expirationDate)}`,
        action: openLinkFn(installmentToPay.link),
        label: 'Pagar agora',
      };
    }
  }
  return {
    dueDate: '',
    action: doNothingFn,
    label: '',
  };
}

function convertOrder({ transactions }) {
  return transactions.map((order) => {
    const paidInstallments = (order.installments
      && order.installments.filter((installment) => installment.paymentStatus === 'PAID').length) || 0;
    const values = getDueDateAndActionAndLabel(order);

    return {
      product: {
        number: order.orderId,
        name: order.productName,
        retailer: `${order.retailerName} em ${moment(order.purchasedDate).format('DD/MM/YYYY')}`,
        image: order.originalImageUrl,
      },
      fullOrder: order,
      date: moment(order.createdAt).format('DD/MM/YYYY'),
      plots: {
        total: order.numberOfInstallments,
        paid: paidInstallments,
      },
      price: moneyFormat(order.totalAmount),
      status: statusVerification.getParsedStatusFromStatus(
        statusVerification.getCurrentStatus(order),
      ),
      dueDate: values.dueDate,
      option: {
        label: values.label,
        action: values.action,
      },
    };
  });
}

export default {
  namespaced: true,
  state: {
    firstTimeApproved: null,
    route: null,
    offers: [],
    loadingMessage: 'Carregando...',
    loading: false,
    profileOption: 0,
    user: null,
    transactions: [],
    transaction: null,
    preApprovedCredit: null,
    orders: [],
    order: { orderId: null },
    sendSecurityCodeStatus: false,
    changeContactStatus: false,
    lastSeenProduct: {},
    lastCheckouts: null,
    lastVerifiedCreditResult: null,
    bvShowed: false,
    bvGroupCode: null,
    bvUserTestId: null,
    finanzeroShowed: false,
    jeittoShowed: false,
  },
  mutations: {
    setRoute(state, route) {
      state.route = route;
    },
    setLoading(state, loading) {
      state.loading = loading;
    },
    setProfileOption(state, profileOption) {
      state.profileOption = profileOption;
    },
    setUser(state, user) {
      state.user = user;
    },
    setTransactions(state, transactions) {
      state.transactions = transactions;
    },
    setTransaction(state, transaction) {
      state.transaction = transaction;
    },
    setPreApprovedCredit(state, preApprovedCredit) {
      state.preApprovedCredit = preApprovedCredit;
    },
    setFirstTimeApproved(state, firstTimeApproved) {
      state.firstTimeApproved = firstTimeApproved;
    },
    setFirstTimeBVLoan(state, bvShowed) {
      state.bvShowed = bvShowed;
    },
    setFirstTimeFinanzero(state, finanzeroShowed) {
      state.finanzeroShowed = finanzeroShowed;
    },
    setFirstTimeJeitto(state, jeittoShowed) {
      state.jeittoShowed = jeittoShowed;
    },
    setOffers(state, offers) {
      state.offers = offers;
    },
    setOrders(state, orders) {
      state.orders = orders;
    },
    setOrder(state, order) {
      state.order = order;
    },
    setSendSecurityCodeStatus(state, sendSecurityCodeStatus) {
      state.sendSecurityCodeStatus = sendSecurityCodeStatus;
    },
    setChangeContactStatus(state, changeContactStatus) {
      state.changeContactStatus = changeContactStatus;
    },
    setLastSeenProduct(state, lastSeenProduct) {
      state.lastSeenProduct = lastSeenProduct;
    },
    setLastCheckouts(state, checkouts) {
      state.lastCheckouts = checkouts;
    },
    setBVGroupCode(state, { groupCode, userTestId }) {
      state.bvGroupCode = groupCode;
      state.bvUserTestId = userTestId;
    },
  },
  actions: {
    setRoute({ commit }, { route }) {
      commit('setRoute', route);
    },
    setProfileOption({ commit }, { profileOption }) {
      commit('setProfileOption', profileOption);
    },
    async getUser({ commit, dispatch }, params) {
      try {
        commit('setLoading', true);
        const { data } = await PortalService.getUser(params);
        commit('setUser', data);
        commit('setLoading', false);
      } catch (error) {
        logger.error(error);
        dispatch('logOutUser', { dispatch });
      }
    },
    logOutUser({ dispatch }) {
      dispatch('auth/setToken', { token: null }, { root: true });
      window.location.href = `${globals().publicUrl}/login`;
    },
    alreadySeeFirstTimeApproved({ commit }) {
      commit('setFirstTimeApproved', false);
    },
    setBvShowed({ commit }) {
      commit('setFirstTimeBVLoan', true);
    },
    setFinanzeroShowed({ commit }) {
      commit('setFirstTimeFinanzero', true);
    },
    setJeittoShowed({ commit }) {
      commit('setFirstTimeJeitto', true);
    },
    async getPreApprovedCredit({ dispatch, commit }) {
      try {
        commit('setLoading', true);
        const { data } = await PortalService.getPreApprovedCredit();
        if (data) {
          commit('setFirstTimeApproved', true);
          commit('setLastSeenProduct', {
            imageUrl: data.checkout?.productImage,
            title: data.checkout?.productName,
            ...calculate({
              value: data.checkout?.totalValue,
              interest: 0.05,
              installments: globals().maxInstallments,
            }),
          });
          commit('setPreApprovedCredit', data);
        }
        commit('setLoading', false);
      } catch (error) {
        logger.error(error);
        if (error && error.response && error.response.status === 401) {
          dispatch('auth/setToken', { token: null }, { root: true });
          window.location.href = `${globals().publicUrl}/login`;
        }
      }
    },
    async getTransactions({ commit, dispatch, state }) {
      try {
        commit('setLoading', true);
        const { data } = await PortalService.getTransactionsWithProduct();
        commit('setTransactions', data);

        if (!Array.isArray(data) || data.length === 0) {
          commit('setTransaction', null);
        }
        dispatch('getLastCheckouts');
        const sorted = data.slice(0)
          .sort((a, b) => moment(a.createdAt).isBefore(b.createdAt));
        const open = sorted.find((el) => el.status === 'OPEN');
        const pending = sorted.find((el) => el.status === 'PENDING');
        commit('setTransaction', open || (pending || null));
        commit('setLoading', false);
      } catch (error) {
        logger.error(error);
        if (error && error.response && error.response.status === 401) {
          dispatch('auth/setToken', { token: null }, { root: true });
          window.location.href = `${globals().publicUrl}/login`;
        }
        throw new Error(error);
      }

      return convertOrder({
        transactions: state.transactions,
      });
    },
    async getTransaction({ commit, dispatch }, { transactionId }) {
      try {
        commit('setLoading', true);
        const { data } = await PortalService.getTransactionWithProduct({
          transactionId,
        });
        commit('setLoading', false);
        return data;
      } catch (error) {
        logger.error(error);
        if (error && error.response && error.response.status === 401) {
          dispatch('auth/setToken', { token: null }, { root: true });
          window.location.href = `${globals().publicUrl}/login`;
        }
        throw new Error(error);
      }
    },
    async runPreApprove({ commit, state, dispatch }) {
      try {
        state.loadingMessage = 'Verificando crédito...';
        commit('setLoading', true);
        await sleep(1000);
        const response = await PortalService
          .preApprove({
            apiKey: globals().apiKey,
            cpf: state.user.cpf,
            phone: state.user.phone,
            email: state.user.email,
            zipCode: state.user.zipCode,
          });
        commit('setLoading', false);
        state.loadingMessage = 'Carregando...';
        state.lastVerifiedCreditResult = response.data.result;
        dispatch('getUser', { dispatch });
        return response.data;
      } catch (error) {
        commit('setLoading', false);
        state.loadingMessage = 'Carregando...';
        logger.error(error);
        throw new Error(error);
      }
    },
    async getOffers({ commit }) {
      try {
        commit('setLoading', true);
        const res = await PortalService.getOffers();
        if (!res) {
          commit('setLoading', false);
          return;
        }
        const { data } = res;
        const offers = data.map((el) => ({
          ...el,
          ...calculate({
            value: el.price,
            interest: 0.05,
            installments: globals().maxInstallments,
          }),
        }))
          .map((offer) => {
            const partner = partners.find((part) => part.title === offer.retailerName);
            const storeUrl = partner?.link || undefined;
            const newOffer = { ...offer, storeUrl };
            return newOffer;
          });
        commit('setOffers', offers);
        commit('setLoading', false);
      } catch (e) {
        logger.error(e);
      }
    },
    async sendSecurityCode({ commit, dispatch }, { type, source }) {
      try {
        commit('setLoading', true);
        const { data } = await PortalService.sendSecurityCode(type, source);
        commit('setSendSecurityCodeStatus', !!data);
        commit('setLoading', false);
      } catch (error) {
        logger.error(error);
        if (error && error.response && error.response.status === 401) {
          dispatch('auth/setToken', { token: null }, { root: true });
          window.location.href = `${globals().publicUrl}/login`;
        }
        throw new Error(error);
      }
    },
    async changeContact({ commit }, { type, body, success }) {
      try {
        commit('setLoading', true);
        const { data } = await PortalService.changeContact(type, body);
        commit('setChangeContactStatus', !!data);
        commit('setLoading', false);
        success();
      } catch (e) {
        logger.error(e);
      }
    },
    async getLastCheckouts({ commit }) {
      const { data } = await PortalService.getLastCheckouts();

      if (Array.isArray(data) && data.length !== 0) {
        commit('setLastCheckouts', data);
      }
    },
    async getBVUserTest({ commit }) {
      const bvGroupCodeFromCookie = JSON.parse(cookieService.readCookie({ name: 'banner-fgts-bv-group-code' }));
      if (bvGroupCodeFromCookie) {
        commit('setBVGroupCode', bvGroupCodeFromCookie);
        return;
      }

      try {
        commit('setLoading', true);
        const { data } = await PortalService.getUserTest('banner-fgts-bv');
        const { userTest } = data;

        if (!userTest) {
          const { data: createUserTestData } = await PortalService.createUserTest('banner-fgts-bv');
          const { userTest: createUserTest } = createUserTestData;
          const value = { groupCode: createUserTest.groupCode, userTestId: createUserTest._id };
          commit('setBVGroupCode', value);

          cookieService.createCookie({
            name: 'banner-fgts-bv-group-code',
            value,
            days: 1,
            isObject: true,
          });
        } else {
          const value = { groupCode: userTest.groupCode, userTestId: userTest._id };
          commit('setBVGroupCode', value);
          cookieService.createCookie({
            name: 'banner-fgts-bv-group-code',
            value,
            days: 1,
            isObject: true,
          });
        }

        commit('setLoading', false);
      } catch (e) {
        logger.error(e);
      }
    },
    async cancelContract({ commit }, id) {
      try {
        commit('setLoading', true);
        await PortalService.cancelContract(id);
      } finally {
        commit('setLoading', false);
      }
    },
  },
};
